import { useState, useEffect } from 'react'
import { Parallax } from 'react-scroll-parallax'
import axios from 'axios'

import './Home.css'

import circularShadow from './img/circularShadow.svg'
import planetLeft from './img/planetLeft.svg'
import planetTop from './img/planetTop.svg'
import shadowTop from './img/shadowTop.svg'
import planetRight from './img/planetRight.svg'
import planetBottom from './img/planetBottom.svg'
import rocket from './img/rocket.svg'

import coins from './img/coins.png'
import secure from './img/secure.svg'
import eco from './img/eco.svg'
import dev from './img/dev.svg'

import triangle from './img/triangle.svg'
import rightShadow from './img/rightShadow.svg'

import mail from './img/email.svg'
import discord from './img/discord.svg'

interface StakedData {
  luna?: number
  ngm?: number
  total?: number
}

function formatNumber(x: number | undefined) {
  return x !== undefined
    ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    : 'loading...'
}

export default function Home() {
  const [staked, setStaked] = useState<StakedData>({})

  useEffect(() => {
    axios.get('https://api.mcontrol.dev/staked').then(({ data }) => {
      setStaked(data)
    })
  }, [])

  return (
    <main>
      <section className="landing safearea">
        <div className="landing__bg" style={{ width: '100vw' }}>
          <Parallax
            speed={15}
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <img src={circularShadow} alt="circular shadow" />
          </Parallax>
          <Parallax
            speed={20}
            style={{ position: 'absolute', top: '45vh', left: 0 }}
          >
            <img src={planetLeft} alt="planet" />
          </Parallax>
          <Parallax
            speed={10}
            style={{ position: 'absolute', top: 50, left: '56vw' }}
          >
            <img src={planetTop} alt="planet" className="noMobile" />
          </Parallax>
          <Parallax
            speed={20}
            style={{ position: 'absolute', top: 150, left: '64vw' }}
          >
            <img src={shadowTop} alt="shadow" className="noMobile" />
          </Parallax>
          <Parallax
            speed={30}
            style={{ position: 'absolute', top: '40vh', right: 0 }}
          >
            <img src={planetRight} alt="planet" />
          </Parallax>
          <Parallax
            speed={10}
            style={{ position: 'absolute', top: '70vh', right: '10vw' }}
            className="planet__bottom"
          >
            <img src={planetBottom} alt="planet" />
          </Parallax>
          <img src={rocket} alt="Rocket" className="rocket" />
        </div>
        <Parallax speed={-3}>
          <div className="landing__container">
            <h1>
              We offer <span>secure</span> and <span>reliable</span> <br />{' '}
              services on the Terra blockchain
            </h1>
            <div className="landing__buttons">
              <a
                href="#delegation"
              >
                Delegate LUNA
              </a>
            </div>
            <p className="landing__stats">
              Our validator is currently securing <br className="noMobile" />{' '}
              {formatNumber(staked.luna)} LUNA, worth <span>{formatNumber(staked.total)} $</span>
            </p>
          </div>
        </Parallax>
      </section>
      <section className="about safearea">
        <div className="about__coins">
          <h3>
            Why should you stake <br />
            with us?
          </h3>
          <img src={coins} alt="coins" />
        </div>
        <div className="about__feathures">
          <div className="feathure">
            <img src={secure} alt="Secure" />
            <div>
              <h4>Secure</h4>
              <p>
                We use the highest security standards to ensure maximum uptime,
                including a sentry architecture with nodes worldwide and 24/7
                monitoring
              </p>
            </div>
          </div>
          <div className="feathure">
            <img src={eco} alt="Eco-friendly" />
            <div>
              <h4>Eco-friendly</h4>
              <p>
                We care about the environment and we are committed to minimizing
                the carbon footprint due to the energy used by our servers
              </p>
            </div>
          </div>
          <div className="feathure">
            <img src={dev} alt="Developers" />
            <div>
              <h4>Experience</h4>
              <p>
                Our validator has been running non-stop since 2021 and we pride ourselves on the internal knowledge and expertise we have built up over the years.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="stake safearea" id="delegation">
        <img src={triangle} alt="Triangle" className="stake__triangle" />
        <h2>How to Delegate using Keplr</h2>
        <div className="delegation__guide">
          <div className="guide__step">
            <div className="step__number">1</div>
            <div className="step__content">
              <h3>Install Keplr Wallet</h3>
              <p>Download the Keplr browser extension from <a href="https://www.keplr.app" target="blank" rel="noopener noreferrer">keplr.app</a> and install it in your browser.</p>
            </div>
          </div>
          <div className="guide__step">
            <div className="step__number">2</div>
            <div className="step__content">
              <h3>Create or Import Wallet</h3>
              <p>Create a new wallet or import an existing one using your recovery phrase. Make sure to save your seed phrase securely.</p>
            </div>
          </div>
          <div className="guide__step">
            <div className="step__number">3</div>
            <div className="step__content">
              <h3>Delegate to our validator</h3>
              <p>Go to <a href="https://wallet.keplr.app/chains/terra?modal=validator&chain=phoenix-1&validator_address=terravaloper1sextn6xd46edrc02tf6d2fysmqw62qu7ng3kkm" target="blank" rel="noopener noreferrer">our validator page</a> on keplr.</p>
            </div>
          </div>
          <div className="guide__step">
            <div className="step__number">4</div>
            <div className="step__content">
              <h3>Delegate Your LUNA</h3>
              <p>Enter the amount of LUNA you want to delegate and confirm the transaction. You'll start earning staking rewards immediately!</p>
            </div>
          </div>
          <div className="guide__step">
            <div className="step__number">5</div>
            <div className="step__content">
              <h3>If you have any questions</h3>
              <p>Visit the official <a href="https://help.keplr.app/wallet-dashboard/5R3bMyjtr33UpY5E29q8p1/stake-with-validators/5UBTq9gUZ9PWGXJT4Q73P7" target="blank" rel="noopener noreferrer">Keplr documentation</a> for more information or send us an email!</p>
            </div>
          </div>
        </div>
      </section>
      <section className="help safearea" style={{ position: 'relative' }}>
        <Parallax
          speed={10}
          style={{ position: 'absolute', top: 30, left: '14vw' }}
        >
          <img src={shadowTop} alt="shadow" />
        </Parallax>
        <Parallax
          speed={20}
          style={{ position: 'absolute', top: 100, right: 0 }}
        >
          <img src={rightShadow} alt="planet" />
        </Parallax>
        <h3>Need help?</h3>
        <p>Our team is at your service for any question.</p>
        <div className="support__container">
          <div className="support">
            <p>Send us an email</p>
            <div className="support__line"></div>
            <a
              href="mailto:info@mcontrol.dev"
              target="blank"
              className="support__email"
            >
              <img src={mail} alt="email" />
              info@mcontrol.dev
            </a>
          </div>
          <div className="support">
            <p>Join Our Discord Server</p>
            <div className="support__line"></div>
            <a
              href="https://discord.gg/ta6hYeEB4k"
              target="blank"
              className="support__discord"
            >
              <img src={discord} alt="Discord" />
            </a>
          </div>
        </div>
      </section>
    </main>
  )
}
